import {
    // Builders
    ComponentBuilder,
    FormBuilder,
    LayoutBuilder,
    PageBuilder,

    // Models
    ComponentSchemaModel,
    LayoutSchemaModel,
    PageSchemaModel,
    ProgressionModel
} from 'ctm-builders';
import { SnippetModel } from 'ctm-snippets';

/**
 * Creates builders for each schema passed and returns the build() method from each builder.
 * @param {Object} builderModelMapping - A mapping of builder types (use to find the builder class) to models (passed to the builder).
 * @returns {Object} A mapping of builder types to builder build() methods.
 */
export const createBuilders = (builderModelMapping) => {
    const builderClassLookup = {
        component: ComponentBuilder,
        form: FormBuilder,
        layout: LayoutBuilder,
        page: PageBuilder
    };

    return Object.keys(builderModelMapping).reduce((builders, builderType) => {
        let model = builderModelMapping[builderType];
        const BuilderClass = builderClassLookup[builderType];

        // Babel 6 supported an invalid spread syntax on objects, which is why this line is necessary
        if (!Array.isArray(model)) model = [{ ...model }];

        const builderInstance = new BuilderClass(...model);
        const builderBuildMethod = builderInstance.build;
        return { ...builders, [builderType]: builderBuildMethod };
    }, {});
};

/**
 * Creates schema models for each schema passed and returns instances of the model classes.
 * @param {Object} modelSchemaMapping - A mapping of model types (use to find the model class) to schemas (passed to the model).
 * @returns {Object} A mapping of model types to model instances.
 */
export const createSchemaModels = (modelSchemaMapping) => {
    const modelClassLookup = {
        componentSchemaModel: ComponentSchemaModel,
        layoutSchemaModel: LayoutSchemaModel,
        pageSchemaModel: PageSchemaModel,
        snippetModel: SnippetModel,
        progressionModel: ProgressionModel
    };

    return Object.keys(modelSchemaMapping).reduce((models, modelType) => {
        let schema = modelSchemaMapping[modelType];
        const ModelClass = modelClassLookup[modelType];

        // Babel 6 supported an invalid spread syntax on objects, which is why this line is necessary
        if (!Array.isArray(schema)) schema = [{ ...schema }];

        const modelInstance = new ModelClass(...schema);
        return { ...models, [modelType]: modelInstance };
    }, {});
};
